import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { NpcGameBackendService } from 'src/app/core/backend-services';
import { isUnauthorized, trainingFailure } from 'src/app/core/backend-services/npc-game/npc-game.backend-service';
import {
  Avatar3DUtil,
  GameInterface,
  GameMessageInterface,
  PlayerInterface,
} from 'src/app/core/interfaces';
import { AnnouncementType, CardColorType } from 'src/app/core/types';
import { randomIntFromInterval, wait } from 'src/app/core/util';
import * as SoundService from 'src/app/core/services/sound/sound.service';
import { GameStateService } from './game-state.service';
import { GameEventService } from './game-event.service';
import { MyPlayerService } from './my-player.service';
import { UserSettingsService } from 'src/app/core/services/user-settings/user-settings.service';
import { BotSpeed } from 'src/app/core/interfaces';

/**
 * Pause (ms, random within the range) before each move of a computer opponent, counted
 * from the moment it is its turn. The client paces the bots: the server plays exactly one
 * bot move per npcMove call and never sleeps, so the game state stays the server's either
 * way. 'fast' is the former (only) pace.
 */
export const BOT_MOVE_DELAY_MS: Record<BotSpeed, [number, number]> = {
  slow: [1800, 2600],
  normal: [1100, 1600],
  fast: [500, 700], // what the former per-poll draw of 500-900 ms came down to
};

/** not replayed when a training is resumed (same as re-entering an online table) */
const RESTORE_SKIPPED = ['gameStart', 'gameCountUpdate', 'marker', 'timedOut', 'didAutoAction', 'timeoutWarning'];

/** why a training ended without the player leaving it */
export type TrainingAbortReason = 'gone' | 'replaced' | 'offline' | 'session';

export const TRAINING_ABORT_MESSAGES: Record<TrainingAbortReason, string> = {
  gone: 'Die Trainingspartie ist nicht mehr verfügbar.',
  replaced: 'In einem anderen Fenster oder Gerät wurde ein neues Training gestartet.',
  offline: 'Keine Verbindung zum Spielserver. Die Trainingspartie wurde beendet.',
  // same wording as the session diagnostics (failure-diagnostics.ts)
  session: 'Deine Anmeldung ist abgelaufen. Bitte melde dich erneut an.',
};

@Injectable({
  providedIn: 'root',
})
export class NPCGameService {
  /** consecutive failures of transient errors (offline, timeout, 5xx) are retried this long */
  public static readonly RETRY_GIVE_UP_MS = 5 * 60 * 1000;

  /**
   * a training ended on its own because the server can no longer continue it (another
   * tab / device started one, TTL expired, redis flushed, older backend) or the server was
   * unreachable for RETRY_GIVE_UP_MS. The local training is already ended when it fires;
   * GameShellService shows the message and the lobby.
   */
  public readonly aborted$ = new Subject<TrainingAbortReason>();

  private player1: PlayerInterface = PlayerInterface.builder()
    .username('Ich')
    .build();
  private player2: PlayerInterface = PlayerInterface.builder()
    .username('Fritz')
    .userID(-10)
    .avatar3d(Avatar3DUtil.generateMaleAvatar('Fritz'))
    .smallProfilePicture('/assets/images/icons/computer.png')
    .isNPC(true)
    .build();
  private player3: PlayerInterface = PlayerInterface.builder()
    .username('Sepp')
    .userID(-11)
    .avatar3d(Avatar3DUtil.generateMaleAvatar('Sepp'))
    .smallProfilePicture('/assets/images/icons/computer.png')
    .isNPC(true)
    .build();
  private player4: PlayerInterface = PlayerInterface.builder()
    .username('Anna')
    .userID(-12)
    .avatar3d(Avatar3DUtil.generateFemaleAvatar('Anna'))
    .smallProfilePicture('/assets/images/icons/computer.png')
    .isNPC(true)
    .build();

  private game: GameInterface = GameInterface.builder().build();
  private timeoutHandle: any;
  private gameRunning: boolean = false;
  /** id of the current training; late answers of an earlier one are dropped */
  private run = 0;
  private currentDifficulty: 'easy' | 'medium' | 'hard' | 'expert' = 'medium';
  /** how many messages of the current game were already emitted */
  private emitted = 0;

  constructor(
    private npcGameBackendService: NpcGameBackendService,
    private state: GameStateService,
    private gameEventService: GameEventService,
    private myPlayerService: MyPlayerService,
    private userSettingsService: UserSettingsService
  ) {}

  /** pause before the next bot move, per the player's bot speed setting (read per move) */
  private botMoveDelay(): number {
    const speed = this.userSettingsService.currentSettings.botSpeed;
    const [min, max] = BOT_MOVE_DELAY_MS[speed] ?? BOT_MOVE_DELAY_MS.normal;
    return randomIntFromInterval(min, max);
  }

  private updatePlayerNames(
    numPlayers: number,
    difficulty: 'easy' | 'medium' | 'hard' | 'expert'
  ): void {
    if (numPlayers === 2) {
      // Schnapsen (2 players)
      switch (difficulty) {
        case 'easy':
          this.player2.username = 'Fritz';
          this.player2.avatar3d = Avatar3DUtil.generateMaleAvatar('Fritz');
          break;
        case 'medium':
          this.player2.username = 'Klaus';
          this.player2.avatar3d = Avatar3DUtil.generateMaleAvatar('Klaus');
          break;
        case 'hard':
          this.player2.username = 'Professor';
          this.player2.avatar3d = Avatar3DUtil.generateMaleAvatar('Professor');
          break;
        case 'expert':
          this.player2.username = 'Hofrat';
          this.player2.avatar3d = Avatar3DUtil.generateMaleAvatar('Hofrat');
          break;
      }
    } else if (numPlayers === 3) {
      // Talonschnapsen (3 players)
      switch (difficulty) {
        case 'easy':
          this.player2.username = 'Sepp';
          this.player2.avatar3d = Avatar3DUtil.generateMaleAvatar('Sepp');
          this.player3.username = 'Anna';
          this.player3.avatar3d = Avatar3DUtil.generateFemaleAvatar('Anna');
          break;
        case 'medium':
          this.player2.username = 'Hans';
          this.player2.avatar3d = Avatar3DUtil.generateMaleAvatar('Hans');
          this.player3.username = 'Gretl';
          this.player3.avatar3d = Avatar3DUtil.generateFemaleAvatar('Gretl');
          break;
        case 'hard':
          this.player2.username = 'Wolfgang';
          this.player2.avatar3d = Avatar3DUtil.generateMaleAvatar('Wolfgang');
          this.player3.username = 'Ingrid';
          this.player3.avatar3d = Avatar3DUtil.generateFemaleAvatar('Ingrid');
          break;
        case 'expert':
          this.player2.username = 'Ferdinand';
          this.player2.avatar3d = Avatar3DUtil.generateMaleAvatar('Ferdinand');
          this.player3.username = 'Theresia';
          this.player3.avatar3d = Avatar3DUtil.generateFemaleAvatar('Theresia');
          break;
      }
    } else if (numPlayers === 4) {
      // Bauernschnapsen (4 players)
      switch (difficulty) {
        case 'easy':
          this.player2.username = 'Maria';
          this.player2.avatar3d = Avatar3DUtil.generateFemaleAvatar('Maria');
          this.player3.username = 'Franz';
          this.player3.avatar3d = Avatar3DUtil.generateMaleAvatar('Franz');
          this.player4.username = 'Helga';
          this.player4.avatar3d = Avatar3DUtil.generateFemaleAvatar('Helga');
          break;
        case 'medium':
          this.player2.username = 'Johann';
          this.player2.avatar3d = Avatar3DUtil.generateMaleAvatar('Johann');
          this.player3.username = 'Elisabeth';
          this.player3.avatar3d =
            Avatar3DUtil.generateFemaleAvatar('Elisabeth');
          this.player4.username = 'Georg';
          this.player4.avatar3d = Avatar3DUtil.generateMaleAvatar('Georg');
          break;
        case 'hard':
          this.player2.username = 'Maximilian';
          this.player2.avatar3d = Avatar3DUtil.generateMaleAvatar('Maximilian');
          this.player3.username = 'Margarete';
          this.player3.avatar3d =
            Avatar3DUtil.generateFemaleAvatar('Margarete');
          this.player4.username = 'Leopold';
          this.player4.avatar3d = Avatar3DUtil.generateMaleAvatar('Leopold');
          break;
        case 'expert':
          this.player2.username = 'Rudolf';
          this.player2.avatar3d = Avatar3DUtil.generateMaleAvatar('Rudolf');
          this.player3.username = 'Josefine';
          this.player3.avatar3d = Avatar3DUtil.generateFemaleAvatar('Josefine');
          this.player4.username = 'Konrad';
          this.player4.avatar3d = Avatar3DUtil.generateMaleAvatar('Konrad');
          break;
      }
    }
  }

  public async playNPCGame(
    numPlayers: 2 | 3 | 4,
    difficulty: 'easy' | 'medium' | 'hard' | 'expert' = 'medium'
  ): Promise<void> {
    return this.runTraining(numPlayers, difficulty);
  }

  /**
   * The training game the server still holds for this account (a reloaded or closed game
   * window), if it is not finished; null when there is none or it cannot be asked.
   */
  public async findUnfinishedGame(): Promise<GameInterface | null> {
    try {
      this.npcGameBackendService.gameUuid = undefined; // whatever the server holds
      const game = await this.npcGameBackendService.getState();
      return game && !game.ended && Array.isArray(game.players) && game.players.length >= 2 ? game : null;
    } catch {
      return null; // 404 trainingNotFound, offline, ...
    }
  }

  /** abandons the unfinished game the player chose not to continue (only that one) */
  public discardUnfinishedGame(game: GameInterface): void {
    Promise.resolve()
      .then(() => this.npcGameBackendService.endGame(game.uuid))
      .catch(() => undefined);
  }

  /** continues `game` (from findUnfinishedGame) where it was left, then plays on as usual */
  public async resumeNPCGame(game: GameInterface): Promise<void> {
    const numPlayers = game.players.length as 2 | 3 | 4;
    return this.runTraining(numPlayers, game.npcDifficulty || 'medium', game);
  }

  private async runTraining(
    numPlayers: 2 | 3 | 4,
    difficulty: 'easy' | 'medium' | 'hard' | 'expert',
    resumeGame?: GameInterface
  ): Promise<void> {
    const run = ++this.run;
    const current = () => this.isCurrent(run);
    this.gameRunning = true;
    this.npcGameBackendService.gameUuid = undefined;
    this.currentDifficulty = difficulty;
    this.state.isNPCGame = true;
    this.updatePlayerNames(numPlayers, difficulty);
    this.state.setPlayers(
      [
        this.myPlayerService.player,
        this.player2,
        this.player3,
        this.player4,
      ].slice(0, numPlayers),
      0
    );

    if (resumeGame) {
      // the view opens pickers (Ansage, Rufen, ...) from the replayed events: wait for it
      await this.gameEventService.whenViewAttached();
      if (!current()) return;
      this.restore(resumeGame);
    }
    this.sendGameEventsNPCGame(run);

    // The first game is the player's own choice and replaces whatever runs on the server.
    // Every following (automatic) game names the game that just ended here, so the server
    // refuses it (409 -> 'replaced') when another tab / device is playing a training
    // meanwhile: an idle old tab never kicks the one the player is using.
    let previousGameUuid: string | undefined = resumeGame?.uuid;
    let resuming = !!resumeGame;

    // A failed request (bad network) must not kill the loop while the training still counts
    // as running: that blocked the reconnect to the next online game. Transient errors are
    // retried (for a while); terminal ones (training gone / replaced) end the training via
    // abort(). Leaving the training (endGame) ends the loop, and late answers are ignored.
    // The server owns the game: we only send our choices and get the (redacted) game back.
    while (current()) {
      if (resuming) {
        resuming = false;
        await this.playUntilEnd(run);
        if (!current()) break;
        await wait(8000);
        continue;
      }
      console.log('Starting new NPC game.');
      const game = await this.retryWhile(run, () =>
        this.npcGameBackendService.newGame(
          numPlayers,
          this.currentDifficulty,
          (window as any).cardsOverride,
          previousGameUuid
        )
      );
      if (!game || !current()) break;
      // the server plays 'hard' instead of a premium level the account may not (or no longer) play
      if (game.npcDifficulty && game.npcDifficulty !== this.currentDifficulty) {
        this.currentDifficulty = game.npcDifficulty;
        this.updatePlayerNames(numPlayers, this.currentDifficulty);
        this.state.setPlayers(
          [this.myPlayerService.player, this.player2, this.player3, this.player4].slice(0, numPlayers),
          0
        );
      }
      previousGameUuid = game.uuid;
      this.applyMyCosmetics(game);
      this.npcGameBackendService.gameUuid = game.uuid;
      this.game = game;
      this.emitted = 0; // a new game: its message list starts from the beginning
      this.game.players = JSON.parse(JSON.stringify(this.state.players));

      await this.playUntilEnd(run);
      if (!current()) break;
      await wait(8000);
    }
  }

  /** lets the bots play (paced) until the current game is over or the training is left */
  private async playUntilEnd(run: number): Promise<void> {
    const current = () => this.isCurrent(run);
    // one random pause per bot turn (drawn anew for every waitingFor), not per poll: a
    // fresh draw every 100 ms would always hit the low end of the range
    let pausedFor: unknown = undefined;
    let pause = 0;
    while (current() && !this.game.ended) {
      if (this.state.isNPCRoundActive) {
        const waitingFor = this.game.rounds[this.game.rounds.length - 1]?.waitingFor;
        if (waitingFor !== pausedFor) {
          pausedFor = waitingFor;
          pause = this.botMoveDelay();
        }
        const timeout =
          this.state.NPCRoundJustStarted && this.game.rounds.length > 1
            ? Math.max(6000, pause)
            : pause;
        if (
          waitingFor &&
          waitingFor.playerIdx !== 0 &&
          waitingFor.timestamp + timeout < Date.now()
        ) {
          // a retried move is harmless: on my turn the server just returns the state
          const next = await this.retryWhile(run, () =>
            this.npcGameBackendService.npcMove()
          );
          if (!next || !current()) break;
          this.accept(next);
          this.state.madeNPCBackendCall();
        }
      }
      await wait(100);
    }
  }

  /**
   * My Stammtisch-Abo look (drink, table texture, badge) as the server sent it with my seat,
   * like at an online table. The server omits defaults and everything without an Abo, so a
   * missing field is removed (an Abo that ran out must not keep its look).
   */
  private applyMyCosmetics(game: GameInterface): void {
    const mine = game.players?.[0] as any;
    const me = this.myPlayerService.player as any;
    if (!me) return;
    for (const key of ['drink', 'tableTexture', 'premiumBadge']) {
      if (mine?.[key] !== undefined) me[key] = mine[key];
      else delete me[key];
    }
    this.state.setPlayers([...this.state.originalPlayers], 0); // in training I am always seat 0
  }

  /**
   * Shows a game taken over from the server (resume): like re-entering an online table,
   * the game start, the current count and only the messages of the running round are
   * replayed; earlier rounds are history. Bot moves continue without the start pause.
   */
  private restore(game: GameInterface): void {
    this.applyMyCosmetics(game);
    this.npcGameBackendService.gameUuid = game.uuid;
    this.game = game;
    this.game.players = JSON.parse(JSON.stringify(this.state.players));
    const messages = game.messages;
    const mine = (m: GameMessageInterface) => m.receiver === 'ALL' || m.receiver === this.state.myIdx;
    const gameStart = [...messages].reverse().find(m => m.type === 'gameStart');
    let roundStart = 0;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === 'roundEnd') {
        roundStart = i + 1;
        break;
      }
    }
    const events: { name: string; data: any }[] = [];
    if (gameStart && mine(gameStart)) events.push({ name: 'gameStart', data: gameStart.data });
    events.push({ name: 'gameCountUpdate', data: { count: game.points } });
    for (const message of messages.slice(roundStart)) {
      if (!mine(message) || RESTORE_SKIPPED.includes(message.type)) continue;
      events.push({ name: message.type, data: message.data });
    }
    this.gameEventService.replay(events);
    this.emitted = messages.length; // everything up to here is shown
    this.state.madeNPCBackendCall();
  }

  /**
   * takes over a game answer of the server. The message list of a game only grows, so an
   * answer of the same game with fewer messages is an older one that arrived late.
   */
  private accept(game: GameInterface): boolean {
    if (!game || !Array.isArray(game.messages)) return false;
    if (game.uuid && this.game.uuid && game.uuid !== this.game.uuid) return false;
    if (game.messages.length < this.game.messages.length) return false;
    const players = this.game.players;
    this.game = game;
    this.game.players = players;
    return true;
  }

  private isCurrent(run: number): boolean {
    return this.gameRunning && run === this.run;
  }

  /**
   * retries a backend call with backoff while training `run` is still running; null once it
   * is not. A terminal failure (training gone / replaced, 4xx, session expired) or transient
   * failures for longer than RETRY_GIVE_UP_MS end the training (abort) instead of retrying
   * forever. A 401 is only terminal once the server confirms over HTTP that the session
   * expired (the socket may just carry a stale one); otherwise it is retried.
   */
  private async retryWhile<T>(run: number, call: () => Promise<T>): Promise<T | null> {
    const started = Date.now();
    for (let attempt = 0; this.isCurrent(run); attempt++) {
      try {
        return await call();
      } catch (e) {
        if (!this.isCurrent(run)) return null; // left meanwhile: a late error changes nothing
        const failure = trainingFailure(e);
        if (!failure && isUnauthorized(e)) {
          const loggedIn = await this.npcGameBackendService.isLoggedIn();
          if (!this.isCurrent(run)) return null;
          if (loggedIn === false) {
            console.warn('NPC backend call: session expired, ending the training:', e);
            this.abort(run, 'session');
            return null;
          }
        }
        if (failure || Date.now() - started >= NPCGameService.RETRY_GIVE_UP_MS) {
          console.warn('NPC backend call failed for good, ending the training:', e);
          this.abort(run, failure ?? 'offline');
          return null;
        }
        console.warn('NPC backend call failed, retrying:', e);
        await wait(Math.min(1000 * 2 ** attempt, 10000));
      }
    }
    return null;
  }

  /** ends training `run` locally (if still running) and reports why */
  private abort(run: number, reason: TrainingAbortReason): void {
    if (!this.isCurrent(run)) return;
    // gone / replaced: there is nothing (of ours) left on the server to free; without a
    // session the server cannot be asked (its game expires with the 6 h TTL)
    this.endGame(reason === 'offline');
    this.aborted$.next(reason);
  }

  /** applies a player action's result only while the same training is still running */
  private async applyAction(label: string, call: () => Promise<GameInterface>): Promise<void> {
    const run = this.run;
    const current = () => this.isCurrent(run);
    try {
      const game = await call();
      if (!current()) return;
      this.accept(game);
      this.state.madeNPCBackendCall();
    } catch (e) {
      console.warn(`NPC ${label} action failed:`, e);
      if (!current()) return;
      // The server may have applied it although the answer got lost (or our state is
      // behind, or the move was rejected): fetch the server's state, otherwise the training
      // could hang on "my turn". If the training is gone / replaced, the resync fails the
      // same way and ends it (retryWhile -> abort).
      const game = await this.retryWhile(run, () => this.npcGameBackendService.getState());
      if (game && current() && this.accept(game)) this.state.madeNPCBackendCall();
    }
  }

  public get isRunning(): boolean {
    return this.gameRunning;
  }

  public endGame(freeServerGame = true): void {
    if (!this.gameRunning) return;
    clearTimeout(this.timeoutHandle);
    this.gameRunning = false;
    this.run++;
    this.state.setPlayers([], 0);
    this.state.reset(true);
    SoundService.stopAllSounds();
    const gameUuid = this.npcGameBackendService.gameUuid;
    this.npcGameBackendService.gameUuid = undefined;
    if (!freeServerGame) return;
    // free the server-side game; an unfinished training is never archived. Only our own
    // game: the server keeps one another tab / device has started meanwhile.
    Promise.resolve()
      .then(() => this.npcGameBackendService.endGame(gameUuid))
      .catch(() => undefined);
  }

  private async sendGameEventsNPCGame(run: number): Promise<void> {
    if (run !== this.run || !this.gameRunning) return;

    // `emitted` is our cursor into the game's (append-only) message list; the server
    // already only sends messages meant for everyone or for me.
    while (run === this.run && this.gameRunning && this.emitted < this.game.messages.length) {
      const message = this.game.messages[this.emitted++];
      if (message.receiver !== 'ALL' && message.receiver !== this.state.myIdx) continue;

      this.gameEventService.emit({
        name: message.type,
        data: message.data,
      });

      message.processed = true;

      if (message.type === 'marker' && message.data.type === 'roundEnd') {
        await wait(4000);
        // training left during the pause: no events into the next game
        if (run !== this.run || !this.gameRunning) return;
      }
    }
    if (run !== this.run || !this.gameRunning) return;
    this.timeoutHandle = setTimeout(() => {
      this.sendGameEventsNPCGame(run);
    }, 100);
  }

  public async playCard(cardNumber: number): Promise<void> {
    await this.applyAction('play card', () =>
      this.npcGameBackendService.playCard(cardNumber)
    );
  }

  public async giveCard(cardNumber: number): Promise<void> {
    await this.applyAction('give card', () =>
      this.npcGameBackendService.giveCard(cardNumber)
    );
  }

  public async marriage(cardNumber: number): Promise<void> {
    await this.applyAction('marriage', () =>
      this.npcGameBackendService.marriage(cardNumber)
    );
  }

  public async changeAtoutCard() {
    await this.applyAction('change atout card', () =>
      this.npcGameBackendService.changeAtoutCard()
    );
  }

  public async zuadrahn() {
    await this.applyAction('zuadrahn', () =>
      this.npcGameBackendService.zuadrahn()
    );
  }

  public async lookTalon(
    myCardsToGiveAway: number[],
    cardsIWantToHave: number[]
  ) {
    await this.applyAction('look talon', () =>
      this.npcGameBackendService.lookTalon(myCardsToGiveAway, cardsIWantToHave)
    );
  }

  public async shout(color: CardColorType | undefined): Promise<void> {
    await this.applyAction('shout', () =>
      this.npcGameBackendService.shout(color)
    );
  }

  public async announce(announcement: AnnouncementType): Promise<void> {
    await this.applyAction('announce', () =>
      this.npcGameBackendService.announce(announcement)
    );
  }

  public async decideKontra(
    doContra: boolean,
    re: boolean = false
  ): Promise<void> {
    await this.applyAction('decide kontra', () =>
      this.npcGameBackendService.decideKontra(doContra, re)
    );
  }
}
